import { AxiosResponse } from 'axios'
import clsx from 'clsx'
import { uniqBy } from 'ramda'
import { useEffect, useState } from 'react'
import { useToast } from 'toast'

import { useVisibilityPreferencesPatch } from 'api/querries/visibility/useVisibilityPreferencesPatch'
import { useVisibilityUsers } from 'api/querries/visibility/useVisibilityUsers'
import { useWhitelistUsersPatch } from 'api/querries/visibility/useWhitelistUsersPatch'
import mainStyles from 'assets/css/main.module.scss'
import { Button } from 'components/common/button/Button'
import { AutoComplete } from 'components/uikit/Autocomplete/AutoComplete'
import { AutocompleteDefaultOption } from 'components/uikit/Autocomplete/AutoComplete.type'
import Avatar from 'components/uikit/Avatar'
import { ListItem } from 'components/uikit/ListItem'
import SingleSelect from 'components/uikit/SingleSelect'
import useDebounce from 'hooks/useDebounce'
import { AnalyticsActionType } from 'types/analytics.type'
import { PractitionerME } from 'types/practitioners'
import { VisibilityPreferences } from 'types/visibility-preferences'
import { ANALYTICS_EVENTS, trackAnalytics } from 'utils/analytics'

import styles from './VisibilityPreferences.module.scss'

interface SidebarProps {
  onClose: () => void
  practitioner: PractitionerME
}

export const VisibilityPreferencesSidebar: React.FC<SidebarProps> = ({
  onClose,
  practitioner,
}: {
  onClose: () => void
  practitioner: PractitionerME
}) => {
  const [who, setWho] = useState<VisibilityPreferences>('private')
  const [usersSelected, setUsersSelected] = useState<(AutocompleteDefaultOption & { image: string })[]>([])
  const [searchUsers, setSearchUsers] = useState<string>('')

  const { mutateAsync: patchVisibilityPreference } = useVisibilityPreferencesPatch()
  const { mutateAsync: patchWhitelistUsers, error: patchWhitelistUsersError } = useWhitelistUsersPatch()

  const { showToast } = useToast()
  const usersSearchParams = useDebounce(searchUsers, 500)

  const { data: visibilityUsers, isLoading: visibilityUsersLoading } = useVisibilityUsers({
    params: {
      limit: 1000,
      page: 1,
      search: usersSearchParams,
    },
    enabled: who === 'partial',
  })

  useEffect(() => {
    if (patchWhitelistUsersError) {
      showToast({ message: 'Failed to update whitelist users', type: 'error' })
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchWhitelistUsersError])

  useEffect(() => {
    if (practitioner) {
      setWho(practitioner.visibilityPreference.preference)
    }
  }, [practitioner])

  useEffect(() => {
    if (practitioner.visibilityWhitelists?.length > 0) {
      setUsersSelected(prev => {
        return uniqBy(
          u => u,
          [
            ...prev,
            ...practitioner.visibilityWhitelists.map(u => ({
              id: u.whitelistedUser.id,
              label: `${u.whitelistedUser.firstname} ${u.whitelistedUser.lastname} • ${u.whitelistedUser.email}`,
              image: u.whitelistedUser.image || '',
            })),
          ],
        )
      })
    }
  }, [practitioner])

  const onSave = async () => {
    patchVisibilityPreference({ preference: who, practitionerId: practitioner.id }).then((res: AxiosResponse) => {
      if (res.status !== 200) return showToast({ message: 'Error updating the visibility preferences', type: 'error' })

      if (who === 'partial') {
        patchWhitelistUsers({
          practitionerId: practitioner.id,
          userIds: usersSelected.map(u => u.id) as string[],
        }).then(() => {
          showToast({ message: 'Visibility preferences succesfully updated with specific users.', type: 'success' })
          onClose()
        })
      } else if (who === 'public') {
        showToast({ message: 'Visibility preferences succesfully updated to public.', type: 'success' })
      } else if (who === 'private') {
        showToast({ message: 'Visibility preferences succesfully updated to private.', type: 'success' })
      }
      trackAnalytics({ type: AnalyticsActionType.action, payload: ANALYTICS_EVENTS.PRACTITIONER_VISIBILITY_SAVED })
      onClose()
    })
  }

  return (
    <>
      <div slot="header" className={clsx(mainStyles.XLHeading, 'text-[#191919]')}>
        Communities of Practitioners Visibility Preferences
      </div>

      <div slot="body" className="flex-1">
        <div className="mt-[24px]">
          <SingleSelect
            required
            hideSearch={true}
            onChange={e => {
              setWho(e.detail.value as any)
            }}
            placeholder="Some (Visible to specific users you select)"
            value={who}
            className={styles.select}
            labelConfig={{
              text: 'Choose to whom your profile is visible',
            }}
          >
            <ListItem value="public">
              <p slot="label">Public (Visible to entire network)</p>
            </ListItem>
            <ListItem value="partial">
              <p slot="label">Some (Visible to specific users you select)</p>
            </ListItem>
            <ListItem value="private">
              <p slot="label">No One (Private)</p>
            </ListItem>
          </SingleSelect>
        </div>
        {who === 'partial' && (
          <div className={clsx('mt-[24px]', styles.dropdownSome)}>
            <AutoComplete
              loading={visibilityUsersLoading}
              required
              multiple
              type="extended"
              value={usersSelected}
              onChange={e => {
                setUsersSelected([
                  ...(e.detail.value?.map((v: any) => ({
                    id: v?.id,
                    label: v?.label,
                    image: visibilityUsers?.find(u => u.id === v.id)?.image || '',
                  })) as (AutocompleteDefaultOption & { image: string })[]),
                ])
              }}
              placeholder="Select specific users"
              labelConfig={{
                text: 'Specify which individuals can view your profile',
              }}
              onSearchValueChange={e => {
                setSearchUsers(e.detail)
              }}
            >
              {visibilityUsers?.length
                ? visibilityUsers.map(user => (
                    <ListItem
                      key={user.id}
                      value={{ id: user.id, label: `${user?.firstname} ${user?.lastname} • ${user?.email}` }}
                      label={`${user.firstname} ${user.lastname}`}
                      className="py-2"
                    >
                      <Avatar size="m" src={user.image || ''} name={`${user.firstname} ${user.lastname}`} />
                      <div slot="label" className={clsx(mainStyles.SBody, 'text-[#121619]')}>
                        {`${user.firstname} ${user.lastname}`}
                      </div>
                      <div className={clsx(mainStyles.SBody, 'text-[#121619]')} slot="caption">
                        {user.email}
                      </div>
                    </ListItem>
                  ))
                : null}
              <div slot="selected-values" className="max-w-[440px]">
                {usersSelected.map(value => (
                  <div>
                    <Avatar size="xs" src={value.image} name={value.label} /> {value.label}
                  </div>
                ))}
              </div>
            </AutoComplete>
          </div>
        )}
      </div>
      <div slot="actions" className="flex flex-row items-center justify-end w-full gap-3">
        <Button text="Cancel" variant="tertiary" onClick={onClose} />
        <Button text="Save" variant="black" onClick={onSave} />
      </div>
    </>
  )
}
